/* eslint-disable max-len */
import React, {useEffect, useRef, useState} from 'react';
import * as PIXI from 'pixi.js';
import '@pixi/unsafe-eval';
import {useTick, AnimatedSprite} from '@pixi/react';
import {calculatePerpendicularPoint, twoBezier} from '../helper';

interface PoopProps {
  onAnimationComplete: () => void;
  stageWidth: number;
  stageHeight: number;
  scaleUpValue: number;
  firstAnimation: PIXI.Texture[];
  secondAnimation: PIXI.Texture[];
  senderPosition: number;
  receiverPosition: number;
  canvasPosition: any;
  playerIconCenterPositions: any;
  fullScreenAnimation: PIXI.Texture[];
  isMuted: boolean;
}

const Poop = React.memo((props: PoopProps) => {
  const {
    stageWidth,
    stageHeight,
    onAnimationComplete,
    scaleUpValue,
    firstAnimation,
    secondAnimation,
    senderPosition,
    receiverPosition,
    canvasPosition,
    playerIconCenterPositions,
    fullScreenAnimation,
    isMuted,
  } = props;
  // TODO
  const startPosition = {
    y: playerIconCenterPositions[senderPosition]?.y - canvasPosition?.top,
    x: playerIconCenterPositions[senderPosition]?.x - canvasPosition?.left,
  };
  const endPosition = {
    y: playerIconCenterPositions[receiverPosition]?.y - canvasPosition?.top,
    x: playerIconCenterPositions[receiverPosition]?.x - canvasPosition?.left,
  };
  // TODO remember to deduct it
  const initialPoint: [number, number] = [startPosition?.x, startPosition?.y];
  const targetPoint: [number, number] = [endPosition?.x, endPosition?.y];

  const [spritePosition, setSpritePosition] = useState({
    x: initialPoint[0],
    y: initialPoint[1],
  });
  const [elapsedTime, setElapsedTime] = useState(0);
  const [currentAnimation, setCurrentAnimation] = useState<number>(1);
  const [curveIntensity, setCurveIntensity] = useState<number>(0);
  const [rot, setRot] = useState<number>(0);

  const audioContextRef = useRef<AudioContext | null>(null);
  const audioBufferRef = useRef<AudioBuffer | null>(null);
  const audioSourceRef = useRef<AudioBufferSourceNode | null>(null);

  const audioFile = '/audio/sticker/poop.mp3';

  useEffect(() => {
    const loadAudio = async () => {
      try {
        const audioContext = new AudioContext();
        const response = await fetch(audioFile);
        const audioData = await response.arrayBuffer();
        const audioBuffer = await audioContext.decodeAudioData(audioData);

        audioContextRef.current = audioContext;
        audioBufferRef.current = audioBuffer;
      } catch (error) {
        console.error('Error loading audio:', error);
      }
    };

    loadAudio();

    return () => {
      // Clean up audio resources
      if (audioSourceRef.current) {
        audioSourceRef.current.stop();
      }
      if (audioContextRef.current) {
        audioContextRef.current.close();
      }
    };
  }, []);

  useEffect(() => {
    const randomIntensity = Math.random() * 0.89 + 0.01;
    // console.log('randomIntensity', randomIntensity)
    setCurveIntensity(0.5);
  }, []);

  useTick(delta => {
    const animationDisplayTime = 100;
    const t = Math.min(elapsedTime / animationDisplayTime, 1);
    if (firstAnimation.length !== 0) {
      if (currentAnimation === 1) {
        setElapsedTime(prevElapsedTime => prevElapsedTime + delta);
        const controlPoint = calculatePerpendicularPoint(
          {x: initialPoint[0], y: initialPoint[1]},
          {x: targetPoint[0], y: targetPoint[1]},
          {width: stageWidth, height: stageHeight},
          curveIntensity
        );
        const [xt, yt] = twoBezier({
          p1: initialPoint,
          p2: targetPoint,
          t,
          c1: [controlPoint.x, controlPoint.y],
        });
        setSpritePosition({x: xt, y: yt});
        setRot(prevRot => prevRot + 0.05 * delta);
        if (t >= 1) {
          setCurrentAnimation(2);
          setElapsedTime(0);
        }
      } else if (currentAnimation === 2) {
        setElapsedTime(prevElapsedTime => prevElapsedTime + delta);
        setSpritePosition({x: endPosition?.x, y: endPosition?.y});

        if (
          !audioSourceRef.current &&
          audioContextRef.current &&
          audioBufferRef.current &&
          !isMuted
        ) {
          const audioContext = audioContextRef.current;
          const audioBuffer = audioBufferRef.current;

          const audioSource = audioContext.createBufferSource();
          audioSource.buffer = audioBuffer;
          audioSource.connect(audioContext.destination);
          audioSource.start();

          audioSourceRef.current = audioSource;
        }
        if (t >= 1) {
          setCurrentAnimation(3);
          setElapsedTime(0);
        }
      } else if (currentAnimation === 3) {
        if (receiverPosition !== 0) {
          onAnimationComplete();
        } else {
          setElapsedTime(prevElapsedTime => prevElapsedTime + delta);
          setSpritePosition({x: stageWidth / 2, y: stageHeight / 2});

          if (t >= 1) {
            onAnimationComplete();
          }
        }
      }
    }
  });

  const getHeight = () => {
    if (currentAnimation === 1) {
      return 65 * scaleUpValue;
    }
    if (currentAnimation === 2) {
      return 65 * scaleUpValue;
    }
    if (currentAnimation === 3) {
      return 600 * scaleUpValue;
    }
  };

  const getAnimation = () => {
    if (currentAnimation === 1) {
      return firstAnimation;
    }
    if (currentAnimation === 2) {
      return secondAnimation;
    }
    if (currentAnimation === 3) {
      return fullScreenAnimation;
    }
  };

  const getanimationspeed = () => {
    if (currentAnimation === 1) {
      return 1;
    }
    if (currentAnimation === 2) {
      return 0.5;
    }
    if (currentAnimation === 3) {
      return 1.15;
    }
  };

  //! otherwise type error
  if (
    firstAnimation.length === 0 ||
    elapsedTime === 0 ||
    (spritePosition.x === 0 && spritePosition.y === 0) ||
    (spritePosition.x === null && spritePosition.y === null)
  ) {
    return null;
  }
  return (
    <AnimatedSprite
      height={getHeight()}
      width={getHeight()}
      animationSpeed={getanimationspeed()}
      textures={getAnimation()}
      anchor={0.5}
      x={spritePosition.x}
      y={currentAnimation === 1 ? spritePosition.y : spritePosition.y}
      isPlaying
      loop={currentAnimation === 1}
      alpha={currentAnimation === 3 ? 0.9 : 1}
    />
  );
});

Poop.displayName = 'Poop';

export default Poop;
